import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Users, Receipt, Clock, FileText, Banknote, TrendingUp } from "lucide-react";
import { SkeletonStatGrid } from "../../components/Skeleton";

const StatCard = ({ label, value, icon: Icon, accent, hint }) => (
  <div className="group rounded-xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 p-5 lift" data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">{label}</div>
        <div className="rule-text text-3xl mt-2 text-zinc-900 dark:text-zinc-100">{value}</div>
        {hint && <div className="text-xs text-zinc-400 mt-1">{hint}</div>}
      </div>
      <div className={`p-2.5 rounded-lg ${accent}`}>
        <Icon size={16} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 p-6 sm:p-8 text-white">
        <svg className="absolute -top-10 -right-10 w-64 h-64 text-orange-300/30" viewBox="0 0 200 200" fill="currentColor" aria-hidden="true">
          <path d="M44.6,-67.1C58.2,-58.2,69.7,-46.1,75.1,-31.8C80.5,-17.6,79.7,-1.1,76.4,14.3C73.2,29.7,67.4,44,57.1,55.2C46.8,66.4,32,74.5,15.6,77.4C-0.8,80.3,-18.7,78,-33.3,70.4C-47.9,62.9,-59.1,50.2,-67.5,35.5C-75.9,20.8,-81.5,4.1,-79.6,-11.7C-77.7,-27.5,-68.4,-42.5,-55.6,-52.1C-42.8,-61.8,-26.6,-66,-10.4,-69.7C5.7,-73.4,11.4,-76.7,23.1,-76.7C34.8,-76.7,52.5,-73.4,62.7,-67.1Z" transform="translate(100 100)" />
        </svg>
        <svg className="absolute inset-0 w-full h-full opacity-15" aria-hidden="true">
          <defs>
            <pattern id="hero-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" />
        </svg>
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[11px] font-semibold mb-3"><TrendingUp size={11} /> Live overview</div>
          <h1 className="rule-text text-3xl sm:text-4xl leading-tight">Good day, Admin.</h1>
          <p className="text-sm text-white/80 mt-2 max-w-md">Here is your real-time operational snapshot — attendance, approvals, and payroll, all at a glance.</p>
        </div>
      </div>

      {/* Stats grid - mobile responsive */}
      {loading ? <SkeletonStatGrid count={5} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 stagger">
          <StatCard label="Employees" value={stats?.total_employees ?? "—"} icon={Users} accent="bg-indigo-50 text-indigo-600" />
          <StatCard label="Present" value={stats?.today_present ?? "—"} icon={Clock} accent="bg-emerald-50 text-emerald-600" hint="today" />
          <StatCard label="Bills" value={stats?.pending_reimbursements ?? "—"} icon={Receipt} accent="bg-orange-50 text-orange-600" hint="pending" />
          <StatCard label="Salary Reqs" value={stats?.pending_salary_requests ?? "—"} icon={Banknote} accent="bg-purple-50 text-purple-600" />
          <StatCard label="Processing" value={stats?.processing_payments ?? "—"} icon={FileText} accent="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300" />
        </div>
      )}

      {/* Bottom info cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100">Salary Rules</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Per-employee monthly salary applied to attendance</p>
            </div>
            <div className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-semibold">Configurable</div>
          </div>
          <div className="space-y-3 text-sm">
            <Row label="Monthly Base" value="Configured per employee" />
            <Row label="Working Days" value="30 / month" />
            <Row label="Per Day Rate" value="Monthly ÷ 30" />
            <Row label="Per Hour Rate" value="Per Day ÷ 4" />
            <Row label="Day Threshold" value="≥ 3 hrs full · < 3 hrs hourly" last />
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-zinc-900 text-white p-6 flex flex-col justify-between min-h-[260px]">
          <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-orange-400/20 blur-2xl"></div>
          <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-indigo-400/20 blur-2xl"></div>
          <div className="relative">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500">Insight</div>
            <h3 className="rule-text text-2xl mt-3 leading-snug">Every hour logged calculates in real time.</h3>
          </div>
          <div className="relative text-xs text-zinc-400 leading-relaxed">
            Auto-payroll uses monthly attendance. Salary transfers run a 24-hour processing window before being marked paid.
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, last }) => (
  <div className={`flex justify-between items-center ${last ? "" : "pb-3 border-b border-zinc-100 dark:border-zinc-800"}`}>
    <span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">{label}</span>
    <span className="font-semibold text-zinc-900 dark:text-zinc-100 text-right">{value}</span>
  </div>
);
